import { Typography, TextField, Card, CardContent, Box } from "@mui/material";
import React, { useState, useEffect } from "react";

const Notes = ({ label, value, onChange, enableEdit = true }) => {
  const [inputValue, setInputValue] = useState(value);

  // Sync local state when parent value changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Debounce the onChange callback to parent
  useEffect(() => {
    const handler = setTimeout(() => {
      if (inputValue !== value) {
        onChange({ target: { value: inputValue } });
      }
    }, 200);
    return () => clearTimeout(handler);
  }, [inputValue]);

  return (
    <Card
      sx={{
        mt: 3,
        p: 2,
        boxShadow: "0px 4px 12px rgba(96, 63, 151, 0.08)",
        borderRadius: 3,
        backgroundColor: "#FFFFFF",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        border: "1px solid rgba(96, 63, 151, 0.08)",
        "&:hover": enableEdit && {
          boxShadow: "0px 8px 24px rgba(96, 63, 151, 0.12)",
          transform: "translateY(-2px)",
        },
        "&:focus-within": enableEdit && {
          boxShadow: "0px 0px 0px 4px rgba(96, 63, 151, 0.12)",
          borderColor: "primary.main",
        },
      }}
      role='region'
      aria-labelledby={`notes-label-${label}`}
    >
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Typography
            variant='h6'
            fontWeight='600'
            gutterBottom
            id={`notes-label-${label}`}
            sx={{
              color: "text.primary",
              fontSize: "1.1rem",
              letterSpacing: "0.5px",
            }}
          >
            {label}
          </Typography>
        </Box>
        {enableEdit ? (
          <TextField
            fullWidth
            multiline
            rows={6}
            maxRows={8}
            placeholder='Enter your notes...'
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            variant='outlined'
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                backgroundColor: "#FAFAFA",
                transition: "all 0.2s ease-in-out",
                "&:hover fieldset": enableEdit && {
                  borderColor: "primary.main",
                  borderWidth: "1px",
                },
                "&.Mui-focused": {
                  backgroundColor: "#FFFFFF",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "primary.main",
                  borderWidth: "2px",
                  boxShadow: "0px 0px 0px 4px rgba(96, 63, 151, 0.08)",
                },
              },
              "& .MuiInputLabel-root": {
                color: "text.secondary",
                fontSize: "0.95rem",
                "&.Mui-focused": {
                  color: "primary.main",
                  fontWeight: "500",
                },
              },
              "& .MuiInputBase-input": {
                fontSize: "0.95rem",
                lineHeight: 1.6,
                color: "text.primary",
                "&::placeholder": {
                  color: "text.secondary",
                  opacity: 0.7,
                },
              },
            }}
            aria-label='Notes input'
            inputProps={{
              "aria-describedby": `notes-label-${label}`,
            }}
            disabled={!enableEdit}
          />
        ) : (
          <Box
            sx={{
              minHeight: "144px",
              borderRadius: 2,
              backgroundColor: "#FAFAFA",
              p: 2,
              border: "1px solid rgba(96, 63, 151, 0.08)",
            }}
          >
            <Typography
              variant='body1'
              sx={{
                fontSize: "0.95rem",
                lineHeight: 1.6,
                color: value ? "text.primary" : "text.secondary",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
              aria-label='Notes view'
            >
              {value || "No notes available"}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default Notes;
